import { type ReactNode, useMemo } from "react"
import { brand } from "@/assets/brand"
import { useCopilotChatInternal } from "@copilotkit/react-core"
import { useCaseDraftStatus } from "@/features/oio/hooks/useCaseDraftStatus"
import { useOIOAgent } from "@/features/oio/hooks/useOIOAgent"
import { useThreadContext } from "@/providers/ThreadProvider"
import { useGenerationStore } from "@/features/oio/generation.store"
import { OIO_STAGES } from "@/features/oio/config/oio.config"
import { useLiveThoughts, deriveActiveStage } from "@/features/oio/hooks/useLiveThoughts"
import { parseAlignedAllegations } from "@/features/oio/lib/allegations"
import { useOrderDraftStore } from "@/features/order-draft/store/order-draft.store"
import { Icon } from "@/components/Icon"
import { MessageList } from "@/features/chat/components/MessageList"
import { Composer } from "@/features/chat/components/Composer"
import type { ChatMessage } from "@/features/chat/components/MessageBubble"

/**
 * The generation pane preserves the pre-draft behaviour that used to live in
 * ChatPanel: live generation messages, the thinking state, and the disabled
 * composer until the first draft is ready.
 *
 * OIO-ONLY: it reads the OIO co-agent (useOIOAgent / useCaseDraftStatus), so it
 * must never mount inside another order type's workspace — a second
 * useCoAgent on the wrong agent reads the wrong state (frontend map G2). A
 * non-OIO workspace composes the same transcript from the order-type-agnostic
 * pieces instead: `deriveActiveStage(..., stages)`,
 * `useLiveThoughts(..., { stages })` and the presentational
 * `GenerationThinking` exported below.
 */
interface DraftGenerationPaneProps {
  hitlNode?: ReactNode
}

function isInternalArtifact(content: string): boolean {
  const t = content.trimStart()
  if (!t.startsWith("{") && !t.startsWith("[")) return false
  return /"(?:__doc_urls|artifact_id|stage_name|scn_fact_findings|document_support_matrix|department_primary_burden_items|corroborative_sources_checked)"/.test(
    t,
  )
}

export function DraftGenerationPane({ hitlNode }: DraftGenerationPaneProps) {
  const { messages: rawMessages, isLoading } = useCopilotChatInternal()
  const { activeThread } = useThreadContext()
  const persistedDraftStatus = activeThread?.metadata?.draft_status as string | undefined
  const hasDraft = Boolean(useOrderDraftStore((s) => s.content))

  const liveDraftStatus = useCaseDraftStatus()
  const draftStatus: string =
    persistedDraftStatus === "draft_completed" ||
    persistedDraftStatus === "draft_failed_terminal"
      ? persistedDraftStatus
      : liveDraftStatus

  const { state: oio } = useOIOAgent()
  const run = useGenerationStore((s) => s.runs[activeThread?.id ?? ""])
  const runRunningHere = run?.status === "running"

  const liveMessages: ChatMessage[] = (rawMessages ?? []).flatMap((m) => {
    if (m.role !== "user" && m.role !== "assistant") return []
    const raw = m.content
    const content =
      typeof raw === "string" ? raw : (raw as { text?: string } | null | undefined)?.text ?? ""
    if (!content) return []
    if (isInternalArtifact(content)) return []

    return [
      {
        id: m.id,
        role: m.role as "user" | "assistant",
        content,
        createdAt: undefined,
      } satisfies ChatMessage,
    ]
  })

  const isGenerating =
    draftStatus === "draft_starting" || draftStatus === "draft_running" || runRunningHere
  const isComposerDisabled =
    isLoading ||
    !hasDraft ||
    (draftStatus !== "draft_completed" && draftStatus !== "draft_not_started")

  const activeStageId = deriveActiveStage(
    oio.current_stage ?? run?.currentStage,
    oio.completed_stages ?? run?.completedStages,
  )
  const stepLabel =
    OIO_STAGES.find((s) => s.id === activeStageId)?.label ?? "Working through the case"
  const allegations = useMemo(
    () =>
      parseAlignedAllegations(oio.aligned_allegations_json).map((a) => ({
        id: a.id,
        title: a.title,
      })),
    [oio.aligned_allegations_json],
  )
  const liveThoughts = useLiveThoughts(
    activeThread?.id ?? null,
    activeStageId,
    isGenerating,
    allegations,
  )

  const draftMissing = draftStatus === "draft_completed" && !hasDraft && !isGenerating

  return (
    <div className="oio-chatpanel">
      {isGenerating ? (
        <div className="chat__stream">
          <GenerationThinking
            stepLabel={stepLabel}
            substage={oio.current_substage ?? run?.currentSubstage ?? undefined}
            thoughts={liveThoughts}
          />
        </div>
      ) : draftMissing ? (
        <div className="chat__stream">
          <div className="empty" style={{ height: "auto", padding: "40px 20px" }}>
            <div className="empty__ico">
              <Icon name="alert" size={22} />
            </div>
            <h3>Draft not available</h3>
            <p>
              This case is complete, but its draft isn't available here. Regenerate it from the
              order panel on the right.
            </p>
          </div>
        </div>
      ) : draftStatus === "draft_failed_terminal" ? (
        <div className="chat__stream">
          <div className="empty" style={{ height: "auto", padding: "40px 20px" }}>
            <div className="empty__ico">
              <Icon name="alert" size={22} />
            </div>
            <h3>Generation was interrupted</h3>
            <p>Use the order panel on the right to retry or report the issue.</p>
          </div>
        </div>
      ) : (
        <MessageList messages={liveMessages} isLoading={false} />
      )}
      {hitlNode}
      <Composer
        onSend={() => {}}
        disabled={isComposerDisabled}
        placeholder="Chat unlocks after the first OIO draft is generated."
      />
    </div>
  )
}

/**
 * The live "thinking" transcript: current step, a spinner and the last few
 * thoughts. Purely presentational (no agent or store reads), so any order
 * type's workspace can render it with its own step label and thoughts.
 */
export function GenerationThinking({
  stepLabel,
  substage,
  thoughts,
  idleLine = "Reading the case and reasoning through each allegation…",
}: {
  stepLabel: string
  substage?: string
  thoughts: string[]
  /** Shown before the first thought when there is no substage. Default is OIO copy. */
  idleLine?: string
}) {
  const recent = thoughts.slice(-6)
  const liveIdx = recent.length - 1

  return (
    <div className="msg">
      <div className="msg__av msg__av--ai">
        <img className="msg__av-logo msg__av-logo--thinking" src={brand.emblem} alt="" />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="msg__name">NitiAssist AI</div>
        <div className="thinking">
          <div className="thinking__head">
            <span className="spinner" style={{ width: 13, height: 13 }} />
            <span className="thinking__step">{stepLabel}</span>
            <span className="thinking__dots" aria-hidden="true">
              <i /> <i /> <i />
            </span>
          </div>

          <ol className="thinking__stream">
            {recent.map((t, i) => {
              const isLive = i === liveIdx
              return (
                <li key={`${i}-${t}`} className={"thought" + (isLive ? " is-live fade-up" : "")}>
                  <span className="thought__mark">
                    {isLive ? (
                      <span className="thought__pulse" />
                    ) : (
                      <Icon name="check" size={12} className="tick" />
                    )}
                  </span>
                  <div className="thought__body">
                    <span className="thought__label">{t}</span>
                  </div>
                </li>
              )
            })}
            {recent.length === 0 && (
              <li className="thought is-live">
                <span className="thought__mark">
                  <span className="thought__pulse" />
                </span>
                <div className="thought__body">
                  <span className="thought__label">
                    {substage ?? idleLine}
                  </span>
                </div>
              </li>
            )}
          </ol>
        </div>
      </div>
    </div>
  )
}
